import { Licensing } from '@adaptiva/core/licensing';
import { pwaGuard } from '@adaptiva/core/pwa';
import { themeSetupGuard } from '@adaptiva/core/themes';
import { authGuard, EJavaClassNames, GlobalPermissions, hasGlobalUiPermissionPrereq, hasVisibleObjectPrereq, licensingGuard, productListGuard, systemConfigSetupGuard } from '@adaptiva/data-service';
import { Routes } from '@angular/router';
import { AdminPortalAppShellComponent } from '../components';
import { lazyLoadingGuard } from '../lazy-loading';

export function getAppRoutes(...children: Routes): Routes {
	return [
		{
			path: '',
			component: AdminPortalAppShellComponent,
			canActivate: [
				pwaGuard,
				authGuard,
				themeSetupGuard,
				lazyLoadingGuard,
				systemConfigSetupGuard,
			],
			canActivateChild: [licensingGuard],
			children: [
				{
					path: 'licensing',
					loadChildren: () => import('../views/licensing').then(m => m.LicensingModule),
					canActivate: [hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_VIEW_LICENSE_STATE)],
				},
				{
					path: 'settings',
					loadChildren: () => import('../views/cloud-settings/cloud-settings.module').then(m => m.CloudSettingsModule),
					data: {
						breadcrumb: 'Cloud Settings'
					}
				},
				{
					path: 'onesite-configmgr',
					loadChildren: () => import('@adaptiva/onesite/onesite-cloud').then(m => m.OneSiteCloudModule)
				},
				{
					path: 'onesite-intune',
					loadChildren: () => import('@adaptiva/onesite/onesite-intune').then(m => m.OneSiteIntuneModule)
				},
				{
					path: 'onesite-workspace-one',
					loadChildren: () => import('@adaptiva/onesite/onesite-vmware').then(m => m.OneSiteVMwareModule)
				},
				{
					path: 'onesite-vmware',
					redirectTo: 'onesite-workspace-one',
					pathMatch: 'prefix',
				},
				{
					path: 'actions',
					loadChildren: () => import('../views/actions').then(m => m.ActionsModule),
					canActivate: [productListGuard(Licensing.getEnterpriseProducts())],
				},
				{
					path: 'sensors',
					title: 'Sensors',
					loadChildren: () => import('@adaptiva/server/views').then(m => m.SensorsModule),
					canActivate: [productListGuard(Licensing.getEnterpriseProducts())],
				},
				{
					path: 'live-wan-downloads',
					loadChildren: () => import('../liveflow/views/liveflow-wrapper/liveflow-wrapper.module').then(m => m.LiveFlowWrappeModule)
				},
				{
					path: 'dashboards',
					loadChildren: () => import('../views/dashboards/dashboards.module').then(m => m.DashboardsModule)
				},
				{
					path: 'content',
					loadChildren: () => import('../views/adaptiva-content').then(m => m.AdaptivaContentModule)
				},
				{
					path: 'content-push',
					loadChildren: () => import('../views/content-push/content-push.module').then(m => m.ContentPushModule)
				},
				{
					path: 'assets',
					loadChildren: () => import('../views/assets/assets-browser.module').then(m => m.AssetsBrowserModule),
					canActivate: [hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_ASSETS_TABLE_VIEW)],
				},
				{
					path: 'schedules',
					loadChildren: () => import('../views/schedule/schedule.module').then(m => m.ScheduleModule)
				},
				{
					path: 'themes',
					loadChildren: () => import('../views/themes/themes.module').then(m => m.ThemesModule),
					canActivate: [hasVisibleObjectPrereq(EJavaClassNames.Theme)],
				},
				{
					path: 'forms',
					loadChildren: () => import('@adaptiva/forms/views').then(m => m.WebFormsModule)
				},
				{
					path: 'surveys',
					loadChildren: () => import('../views/surveys/surveys.module').then(m => m.SurveysModule)
				},
				{
					path: 'patching',
					loadChildren: () => import('@adaptiva/patching/views').then(m => m.PatchingModule)
				},
				{
					path: 'inventory',
					loadChildren: () => import('@adaptiva/telemetry/views').then(m => m.TelemetryModule)
				},
				{
					path: 'workflows',
					loadChildren: () => import('@adaptiva/workflows/views').then(m => m.WorkflowModule),
					canActivate: [productListGuard(Licensing.getEnterpriseProducts())],
				},
				{
					path: 'tool-foundry',
					loadChildren: () => import('@adaptiva/workflows/views').then(m => m.ToolFoundryModule),
					canActivate: [productListGuard(Licensing.getEnterpriseProducts())],
				},
				{
					path: 'endpoint-health',
					loadChildren: () => import('@adaptiva/endpoint-health/views').then(m => m.OnesiteHealthModule)
				},
				{
					path: 'memory-pipeline',
					loadChildren: () => import('../views/memory-pipeline-page/memory-pipeline-page.module')
						.then(m => m.MemoryPipelinePageComponentModule)
				},
				{
					path: 'sensor-action-policies',
					loadChildren: () => import('../views/sensor-action-execution-policy/sensor-action-execution-policy.module')
						.then(m => m.ServerActionExecutionPolicyModule)
				},
				{
					path: 'integrations',
					loadChildren: () => import('../views/integrations/platform-integrations.module').then(m => m.PlatformIntegrationsModule),
				},
				{
					path: 'audit-logs',
					title: 'Audit Logs',
					loadComponent: () => import('../views/audit-logs/audit-logs-dashboard.component').then(c => c.AuditLogsDashboardComponent),
					canActivate: [
						hasGlobalUiPermissionPrereq(GlobalPermissions.PERMISSIONS_FOR_LOGS_DOWNLOAD),
					]
				},
				...children,
				{
					path: '',
					pathMatch: 'full',
					loadChildren: () => import('../views/admin-portal-home/admin-portal-home.module').then(m => m.AdminPortalHomeModule)
				},
				{
					path: '**',
					children: [
						{
							path: '',
							loadChildren: () => import('../views/404/page-not-found.module').then(m => m.PageNotFoundModule)
						},
					]
				},
			],
			data: {
				breadcrumb: 'App Shell'
			}
		}
	];
}
